<script lang="ts">
  import { goto } from "$app/navigation";
  import { TableAPI } from "$lib/api/table";
  import { TemplateAPI, type TemplateForGetAllResponse } from "$lib/api/template";
  import { CreateTableFromAIAPI, type CreateTableFromAIProgressUpdate } from "$lib/api/gen-sheet-data";
  import { Button } from "$lib/components/ui";
  import * as Dialog from "$lib/components/ui/dialog";
  import * as Popover from "$lib/components/ui/popover";
  import { ScrollArea } from "$lib/components/ui/scroll-area";
  import { authStore } from "$lib/stores/authStore";
  import { tableStore } from "$lib/stores/table-store";
  import { tableHistoryStore } from "$lib/stores/tableHistoryStore";
  import { cn } from "$lib/utility";
  import { ArrowLeft, FileText, Globe, Library, Loader2, ShoppingCart, Table, Users, Wand2 } from "lucide-svelte";
  import posthog from "posthog-js";
  import { onMount } from "svelte";
  import { toast } from "svelte-sonner";
  import TemplateCard from "../../../routes/(app)/marketplace/templates/_components/TemplateCard.svelte";
  import ShortcutKey from "./ShortcutKey.svelte";

  let selectingFromTemplate = $state(false);
  let isLoading = $state(false);
  let prioritizedTemplates: TemplateForGetAllResponse[] = $state([]);
  let personalTemplates: TemplateForGetAllResponse[] = $state([]);
  let teamTemplates: TemplateForGetAllResponse[] = $state([]);
  let publicTemplates: TemplateForGetAllResponse[] = $state([]);
  let selectedTemplateId: string | null = $state(null);
  let openPopoverId: string | null = $state(null);

  // AI Generation states
  let aiPrompt = $state("");
  let isGeneratingAI = $state(false);
  let generationProgress = $state("");
  let generationError = $state("");
  let elapsedSeconds = $state(0);
  let eventSource: EventSource | null = $state(null);

  // Category data
  const categories = [
    {
      id: "automate-pdfs",
      name: "Automate PDFs",
      icon: FileText,
      gradient:
        "from-red-50 via-orange-50 to-yellow-50 dark:from-red-950/40 dark:via-orange-950/40 dark:to-yellow-950/40",
      border: "border-red-200/60 dark:border-orange-800/40",
      text: "text-red-700 dark:text-orange-300",
      hover:
        "hover:from-red-100 hover:via-orange-100 hover:to-yellow-100 dark:hover:from-red-950/60 dark:hover:via-orange-950/60 dark:hover:to-yellow-950/60",
      prompts: [
        "Take my PDF's and extract these fields...",
        "Take my invoice pdf's and categorize them and extract...",
        "Take my job applicant resumes and extract...",
      ],
    },
    {
      id: "automate-sales",
      name: "Automate Sales",
      icon: Users,
      gradient: "bg-indigo-50/80 dark:bg-indigo-950/30",
      border: "border-indigo-200/50 dark:border-indigo-800/30",
      text: "text-indigo-700 dark:text-indigo-300",
      hover: "hover:bg-indigo-100/80 dark:hover:bg-indigo-950/50",
      prompts: [
        "Enrich my sales leads and research...",
        "Automate dm's to my Linkedin Leads",
        "Automate cold emails to my leads",
      ],
    },
    {
      id: "web-scraping",
      name: "Web Scraping",
      icon: Globe,
      gradient: "bg-teal-50/80 dark:bg-teal-950/30",
      border: "border-teal-200/50 dark:border-teal-800/30",
      text: "text-teal-700 dark:text-teal-300",
      hover: "hover:bg-teal-100/80 dark:hover:bg-teal-950/50",
      prompts: [
        "Scrape Linkedin for me and extract...",
        "Scrape Youtube for me and summarize...",
        "Scrape my website for me and extract...",
      ],
    },
    {
      id: "ecommerce",
      name: "eCommerce",
      icon: ShoppingCart,
      gradient: "bg-purple-50/80 dark:bg-purple-950/30",
      border: "border-purple-200/50 dark:border-purple-800/30",
      text: "text-purple-700 dark:text-purple-300",
      hover: "hover:bg-purple-100/80 dark:hover:bg-purple-950/50",
      prompts: ["Summarize Amazon reviews and track pricing"],
    },
  ];

  const { setShowCreateTableDialog } = tableStore;

  const user = authStore.getUser();
  const userId = user?.id || 0;

  function handleKeyPress(event: KeyboardEvent) {
    if (!$tableStore.showCreateTableDialog) return;

    // Don't trigger shortcuts if user is typing in an input field
    const activeElement = document.activeElement;
    if (
      activeElement &&
      (activeElement.tagName === "INPUT" ||
        activeElement.tagName === "TEXTAREA" ||
        activeElement.hasAttribute("contenteditable"))
    ) {
      return;
    }

    if (event.key.toLowerCase() === "b") {
      createBlankTable();
    } else if (event.key.toLowerCase() === "t") {
      selectingFromTemplate = true;
    }
  }

  onMount(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  });

  async function loadPrioritizedTemplates() {
    const prioritizedTemplatesResponse = await TemplateAPI.getTemplates({
      showInCreateDialog: true,
    });

    prioritizedTemplates = prioritizedTemplatesResponse;
  }

  async function loadTemplates() {
    isLoading = true;
    try {
      const allTemplates = await TemplateAPI.getTemplates({});

      personalTemplates = allTemplates.filter(
        (template) => template.createdBy === userId && template.visibility === "private",
      );
      teamTemplates = allTemplates.filter((template) => template.visibility === "team");
      publicTemplates = allTemplates.filter((template) => template.visibility === "public");
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    } finally {
      isLoading = false;
    }
  }

  $effect(() => {
    if (selectingFromTemplate) {
      loadTemplates();
    }
  });

  $effect(() => {
    if (!selectingFromTemplate) {
      loadPrioritizedTemplates();
    }
  });

  async function createBlankTable() {
    if (!userId) return;
    try {
      const data = await TableAPI.createTable({
        name: "New sheet",
        numColumns: 10,
        numRows: 10,
      });

      const now = new Date();
      const newTable = {
        id: data.id,
        publicId: data.id,
        name: "New sheet",
        visibility: "private" as const,
        createdAt: now,
        updatedAt: now,
        ownerId: String(userId),
      };

      console.log("Adding new table to store with current date:", newTable);

      tableHistoryStore.appendTables([newTable]);
      setShowCreateTableDialog(false);
      goto(`/table/${data.id}`);
    } catch (error) {
      toast.error("Error creating table");
      console.error("Error creating table", error);
    }
  }

  async function createTableFromTemplate() {
    if (!selectedTemplateId) return;
    if (!userId) return;

    try {
      const data = await TableAPI.createTable({
        name: "New sheet",
        numColumns: 10,
        numRows: 10,
        templateId: selectedTemplateId,
      });

      const now = new Date();
      const newTable = {
        id: data.id,
        publicId: data.id,
        name: "New sheet",
        visibility: "private" as const,
        createdAt: now,
        updatedAt: now,
        ownerId: String(userId),
      };

      console.log("Adding new table to store with current date:", newTable);

      tableHistoryStore.appendTables([newTable]);
      setShowCreateTableDialog(false);
      goto(`/table/${data.id}`);
    } catch (error) {
      toast.error("Error creating table");
      console.error("Error creating table", error);
    }
  }

  const handleRunFailed = (error: string) => {
    toast.error("Failed to generate table with AI");
    console.error("AI generation failed:", error);
    posthog.capture("ai_table_creation_failed", {
      category: "table_creation",
      prompt_length: aiPrompt.length,
      user_id: userId,
      generation_time_seconds: elapsedSeconds,
    });
  };

  function generateTableWithAI() {
    if (!aiPrompt.trim()) {
      toast.error("Please enter a description for your table");
      return;
    }

    isGeneratingAI = true;
    generationProgress = "Starting AI generation...";
    generationError = "";
    elapsedSeconds = 0;

    // Close existing connection if any
    if (eventSource) {
      eventSource.close();
    }

    eventSource = CreateTableFromAIAPI.createTableFromAI(aiPrompt, (update: CreateTableFromAIProgressUpdate) => {
      elapsedSeconds = Math.floor(update.elapsedTime / 1000);

      switch (update.status) {
        case "error":
          isGeneratingAI = false;
          generationError = update.error || "Unknown error occurred";
          handleRunFailed(generationError);
          eventSource = null;
          break;
        case "completed":
          if (update.tableId) {
            toast.success("Table created successfully!");

            // Add table to history and navigate
            const now = new Date();
            const newTable = {
              id: update.tableId,
              publicId: update.tableId,
              name: "AI Generated Table",
              visibility: "private" as const,
              createdAt: now,
              updatedAt: now,
              ownerId: String(userId),
            };

            tableHistoryStore.appendTables([newTable]);
            setShowCreateTableDialog(false);
            goto(`/table/${update.tableId}`);

            posthog.capture("ai_table_created", {
              category: "table_creation",
              prompt_length: aiPrompt.length,
              user_id: userId,
              generation_time_seconds: elapsedSeconds,
              table_id: update.tableId,
            });
          }
          isGeneratingAI = false;
          eventSource = null;
          break;
        default:
          generationProgress = update.status;
          break;
      }
    });
  }

  // Cleanup on component destroy
  $effect(() => {
    return () => {
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
    };
  });
</script>

<Dialog.Root
  open={$tableStore.showCreateTableDialog}
  onOpenChange={(value) => {
    setShowCreateTableDialog(value);
    selectingFromTemplate = false;
  }}
>
  <Dialog.Content
    id="create-table-dialog"
    class={cn(
      "w-full gap-1 border-sidebar-border max-h-[90vh] overflow-y-auto transition-none dark:border-white/20",
      selectingFromTemplate ? "h-[90vh] overflow-y-auto sm:max-w-5xl" : "sm:max-w-2xl",
    )}
  >
    <Dialog.Header>
      <Dialog.Title class="text-lg font-medium">
        {#if !selectingFromTemplate}
          Create Table
        {:else}
          <div class="flex items-center gap-2">
            <Button
              onclick={() => (selectingFromTemplate = false)}
              variant="outline"
              class={cn(
                "flex h-7 items-center justify-center gap-1.5 !rounded-full text-2xs font-normal text-theme-text-primary dark:border-white dark:border-white/20 dark:bg-theme-background-tertiary dark:hover:border-theme-text-secondary dark:hover:text-theme-text-primary",
                "size-7",
              )}
            >
              <ArrowLeft size={16} />
            </Button>
            <span>Select a template</span>
          </div>
        {/if}
      </Dialog.Title>
      <Dialog.Description>
        {#if !selectingFromTemplate}
          Create table with AI, start with a blank table or use a template.
        {:else}
          <span class="ml-11">Pick a template to get started.</span>
        {/if}
      </Dialog.Description>
    </Dialog.Header>
    {#if !selectingFromTemplate}
      <!-- AI Table Creation Section -->
      <div class="mt-6 space-y-4">
        <div
          class="relative bg-gradient-to-r from-violet-50 via-purple-50 to-pink-50 dark:from-violet-950/30 dark:via-purple-950/30 dark:to-pink-950/30 p-4 rounded-xl border border-violet-200/50 dark:border-violet-800/30"
        >
          <div class="flex items-center gap-3 mb-3">
            <div class="p-2 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 shadow-lg">
              <Wand2 class="size-4 text-white" />
            </div>
            <div>
              <h3
                class="font-semibold text-sm bg-gradient-to-r from-violet-700 via-purple-700 to-pink-700 bg-clip-text text-transparent dark:from-violet-300 dark:via-purple-300 dark:to-pink-300"
              >
                Create with AI
              </h3>
              <p class="text-xs text-muted-foreground">Describe the table you want to create</p>
            </div>
          </div>

          <div class="space-y-3">
            <div class="relative">
              <textarea
                bind:value={aiPrompt}
                disabled={isGeneratingAI}
                placeholder="e.g., 'Create a project tracker with columns for task name, status, priority, and due date...'"
                class="w-full h-20 px-3 py-2 text-sm bg-background/80 border border-violet-200/50 dark:border-violet-800/30 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 transition-all duration-200 placeholder:text-muted-foreground/60 backdrop-blur-sm disabled:opacity-50 disabled:cursor-not-allowed"
              ></textarea>
            </div>

            <!-- Quick Categories -->
            <div class="space-y-2">
              <div class="flex flex-wrap items-center justify-center gap-2">
                {#each categories as category}
                  <Popover.Root
                    open={openPopoverId === category.id}
                    onOpenChange={(open) => (openPopoverId = open ? category.id : null)}
                  >
                    <Popover.Trigger>
                      <button
                        type="button"
                        disabled={isGeneratingAI}
                        class={cn(
                          "inline-flex items-center gap-1.5 text-xs font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-sm",
                          category.id === "automate-pdfs" ? "px-3 py-1.5 bg-gradient-to-r" : "px-2.5 py-1",
                          category.gradient,
                          "border",
                          category.border,
                          category.text,
                          category.hover,
                        )}
                      >
                        <svelte:component this={category.icon} class="size-3" />
                        <span>{category.name}</span>
                      </button>
                    </Popover.Trigger>
                    <Popover.Content class="w-96 p-3 border border-sidebar-border rounded-lg">
                      <div class="space-y-2">
                        <h4 class="font-medium text-sm">{category.name}</h4>
                        <div class="space-y-1">
                          {#each category.prompts as prompt}
                            <button
                              type="button"
                              disabled={isGeneratingAI}
                              onclick={() => {
                                aiPrompt = prompt;
                                openPopoverId = null;
                              }}
                              class="w-full text-left text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 p-2 rounded transition-colors duration-200"
                            >
                              {prompt}
                            </button>
                          {/each}
                        </div>
                      </div>
                    </Popover.Content>
                  </Popover.Root>
                {/each}
              </div>
            </div>

            <div class="flex justify-end items-center gap-2">
              <p class="text-xs text-muted-foreground/80">Generally takes about a minute</p>
              <Button
                disabled={isGeneratingAI || !aiPrompt.trim()}
                onclick={generateTableWithAI}
                size="sm"
                class="relative rounded-xl overflow-hidden group bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div
                  class="absolute inset-0 bg-gradient-to-r from-violet-400 to-pink-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                ></div>
                <div class="relative flex items-center gap-1.5">
                  {#if isGeneratingAI}
                    <Loader2 class="size-3 animate-spin" />
                  {:else}
                    <Wand2 class="size-3" />
                  {/if}
                  <span class="text-xs font-medium">
                    {isGeneratingAI ? `Generating... (${elapsedSeconds}s)` : "Generate Table"}
                  </span>
                </div>
              </Button>
            </div>
          </div>
        </div>

        <!-- Separator -->
        {#if !isGeneratingAI}
          <div class="relative my-6">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-sidebar-border dark:border-white/20"></div>
            </div>
            <div class="relative flex justify-center text-xs uppercase">
              <span class="bg-background px-4 text-muted-foreground">Or</span>
            </div>
          </div>
        {/if}
      </div>

      {#if !isGeneratingAI}
        <div class="flex items-center gap-3">
          <button
            type="button"
            id="create-blank-table-button"
            onclick={() => createBlankTable()}
            class="flex relative min-h-28 rounded-lg w-1/2 flex-col items-center justify-center gap-1 border border-sidebar-border py-5 hover:border-black dark:border-white dark:border-white/20"
          >
            <ShortcutKey key="B" className="absolute top-2 right-2 rounded-md" />
            <Table size={24} class="mb-4 size-6 stroke-1 text-theme-text-secondary" />
            <span class="text-sm font-medium">Blank</span>
            <p class="text-sm text-theme-text-secondary">Start with a blank table.</p>
          </button>

          <button
            type="button"
            onclick={() => (selectingFromTemplate = true)}
            class="flex min-h-28 relative w-1/2 flex-col rounded-lg items-center justify-center gap-1 border border-sidebar-border py-5 hover:border-black dark:border-white dark:border-white/20"
          >
            <ShortcutKey key="T" className="absolute top-2 right-2 rounded-md" />

            <Library size={24} class="mb-4 size-6 stroke-1 text-theme-text-secondary" />
            <span class="text-sm font-medium">Template</span>
            <p class="text-sm text-theme-text-secondary">Start with a template.</p>
          </button>
        </div>
      {/if}

      <!-- {#if !isGeneratingAI && prioritizedTemplates.length > 0}
        <div class="relative mt-5 border-t border-sidebar-border pt-4">
          <p class="absolute -top-3 left-1/2 -translate-x-1/2 bg-background px-5 text-xs text-theme-text-secondary">
            Or use these recommended templates
          </p>
          <div class="grid grid-cols-2 gap-3">
            {#each prioritizedTemplates as template}
              <button
                type="button"
                class="w-full"
                onclick={() => {
                  selectedTemplateId = template.id;
                  createTableFromTemplate();
                }}
              >
                <TemplateCard
                  thumbnailUrl={template.thumbnailUrl}
                  creator={template.creator}
                  id={template.id}
                  name={template.title}
                  description={template.description}
                  categories={template.categories}
                  isOfficial={template.isOfficial}
                />
              </button>
            {/each}
          </div>
        </div>
      {/if} -->
    {:else}
      <ScrollArea class="mt-5 flex h-[69vh] flex-col">
        {#if isLoading}
          <div class="flex justify-center py-8">
            <Loader2 class="size-8 animate-spin text-theme-text-secondary" />
          </div>
        {:else}
          <div class="flex flex-col gap-6">
            {#each [{ title: "My Private Templates", templates: personalTemplates }, { title: "Team Templates", templates: teamTemplates }, { title: "Public Templates", templates: publicTemplates }] as section}
              {#if section.templates.length > 0}
                <section>
                  <h3 class="mb-3 text-xs font-light uppercase text-theme-text-secondary">{section.title}</h3>
                  <div class="grid grid-cols-3 items-start gap-3">
                    {#each section.templates as template}
                      <button
                        type="button"
                        class="w-full"
                        onclick={() =>
                          selectedTemplateId === template.id
                            ? (selectedTemplateId = null)
                            : (selectedTemplateId = template.id)}
                      >
                        <TemplateCard
                          thumbnailUrl={template.thumbnailUrl}
                          id={template.id}
                          creator={template.creator}
                          name={template.title}
                          description={template.description}
                          categories={template.categories}
                          isOfficial={template.isOfficial}
                          className={cn(
                            "text-left",
                            selectedTemplateId === template.id
                              ? "border-theme-dark26 dark:border-white dark:hover:border-white"
                              : "",
                          )}
                        />
                      </button>
                    {/each}
                  </div>
                </section>
              {/if}
            {/each}

            {#if !personalTemplates.length && !teamTemplates.length && !publicTemplates.length}
              <div class="py-8 text-center text-theme-text-secondary">No templates found</div>
            {/if}
          </div>
        {/if}
      </ScrollArea>

      <Button
        disabled={!selectedTemplateId}
        class={cn(
          "justift-center flex items-center gap-1.5 !rounded-base bg-black px-1.5 py-1 font-normal text-white dark:border-white dark:border-white/20 dark:bg-theme-green dark:text-black dark:hover:text-black",
        )}
        aria-label="Create table"
        onclick={() => createTableFromTemplate()}
      >
        Create Table
      </Button>
    {/if}
  </Dialog.Content>
</Dialog.Root>
